import { useState } from 'react'
import { Search, Users, ChevronDown, Clock, Trophy, ExternalLink, Flag, Download, AlertTriangle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'
import { HandTimeline, MiniCard } from '@/components/admin/HandTimeline'
import type { CardProps, Street, PotLine } from '@/components/admin/HandTimeline'

type HandStatus = 'normal' | 'dispute' | 'frozen'
type FilterType = 'all' | 'today' | 'dispute' | 'frozen'

interface MockHand { id: string; room: string; stakes: string; time: string; pot: number; winner: string | null; players: number; result: string; status: HandStatus }

const mockHands: MockHand[] = [
  { id: 'H-7842', room: 'A1F3', stakes: '1/2', time: '14:23', pot: 4500, winner: '小明', players: 5, result: '兩對', status: 'normal' },
  { id: 'H-7841', room: 'B2G4', stakes: '5/10', time: '14:21', pot: 28000, winner: 'Lucky88', players: 6, result: '同花', status: 'normal' },
  { id: 'H-7840', room: 'A1F3', stakes: '1/2', time: '14:18', pot: 1200, winner: '阿花', players: 4, result: '高牌', status: 'dispute' },
  { id: 'H-7839', room: 'D4J6', stakes: '1/2', time: '14:15', pot: 8800, winner: null, players: 7, result: '凍結', status: 'frozen' },
  { id: 'H-7838', room: 'B2G4', stakes: '5/10', time: '14:12', pot: 15000, winner: 'PokerKing', players: 5, result: '順子', status: 'normal' },
  { id: 'H-7837', room: 'F6L8', stakes: '5/10', time: '14:08', pot: 6200, winner: 'Sarah_HK', players: 4, result: '三條', status: 'normal' },
]

const filters: { id: FilterType; label: string }[] = [{ id: 'all', label: '全部' }, { id: 'today', label: '今日' }, { id: 'dispute', label: '爭議' }, { id: 'frozen', label: '凍結' }]

const statusConfig: Record<HandStatus, { label: string; cls: string }> = { normal: { label: '正常', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' }, dispute: { label: '爭議', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/25' }, frozen: { label: '凍結', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/25' } }

const mockCommunity: CardProps[] = [
  { rank: 'K', suit: 'spade' }, { rank: '9', suit: 'heart' }, { rank: '4', suit: 'club' },
  { rank: 'J', suit: 'diamond' }, { rank: '2', suit: 'spade' },
]

const mockPlayerCards: { name: string; cards: CardProps[] }[] = [
  { name: '小明', cards: [{ rank: 'K', suit: 'heart' }, { rank: '4', suit: 'heart' }] },
  { name: 'Lucky88', cards: [{ rank: 'A', suit: 'spade' }, { rank: 'Q', suit: 'club' }] },
  { name: '阿花', cards: [{ rank: '7', suit: 'diamond' }, { rank: '7', suit: 'club' }] },
]

interface HandDetail {
  id: string
  timestamp: string
  streets: Street[]
  potDistribution: PotLine[]
}

const mockHandDetails: Record<string, HandDetail> = {
  'H-7842': {
    id: 'H-7842', timestamp: '2026-02-07 14:23:45',
    streets: [
      { name: 'Preflop', communityCards: [], actions: [
        { player: '小明', action: 'raise', amount: '4BB' },
        { player: 'Lucky88', action: 'call' },
        { player: '阿花', action: 'call' },
      ]},
      { name: 'Flop', communityCards: [{ rank: 'K', suit: 'spade' }, { rank: '9', suit: 'heart' }, { rank: '4', suit: 'club' }], actions: [
        { player: '小明', action: 'bet', amount: '8BB' },
        { player: 'Lucky88', action: 'fold' },
        { player: '阿花', action: 'call' },
      ]},
      { name: 'Turn', communityCards: [{ rank: 'J', suit: 'diamond' }], actions: [
        { player: '小明', action: 'check' },
        { player: '阿花', action: 'bet', amount: '12BB' },
        { player: '小明', action: 'call' },
      ]},
      { name: 'River', communityCards: [{ rank: '2', suit: 'spade' }], actions: [
        { player: '小明', action: 'all-in' },
        { player: '阿花', action: 'fold' },
      ]},
    ],
    potDistribution: [
      { label: '小明 (贏家)', amount: 4275 },
      { label: '抽水', amount: 225 },
    ],
  },
  'H-7841': {
    id: 'H-7841', timestamp: '2026-02-07 14:21:12',
    streets: [
      { name: 'Preflop', communityCards: [], actions: [
        { player: 'Lucky88', action: 'raise', amount: '3BB' },
        { player: 'PokerKing', action: '3-bet', amount: '9BB' },
        { player: 'Lucky88', action: 'call' },
      ]},
      { name: 'Flop', communityCards: [{ rank: '7', suit: 'heart' }, { rank: '10', suit: 'heart' }, { rank: '3', suit: 'heart' }], actions: [
        { player: 'Lucky88', action: 'check' },
        { player: 'PokerKing', action: 'bet', amount: '15BB' },
        { player: 'Lucky88', action: 'call' },
      ]},
      { name: 'Turn', communityCards: [{ rank: '5', suit: 'club' }], actions: [
        { player: 'Lucky88', action: 'check' },
        { player: 'PokerKing', action: 'all-in' },
        { player: 'Lucky88', action: 'call' },
      ]},
      { name: 'River', communityCards: [{ rank: 'K', suit: 'diamond' }], actions: [] },
    ],
    potDistribution: [
      { label: 'Lucky88 (贏家)', amount: 26600 },
      { label: '抽水', amount: 1400 },
    ],
  },
}

// Generate a default detail for hands without specific mock data
const getHandDetail = (hand: MockHand): HandDetail => {
  if (mockHandDetails[hand.id]) return mockHandDetails[hand.id]
  return {
    id: hand.id, timestamp: `2026-02-07 ${hand.time}:00`,
    streets: [
      { name: 'Preflop', communityCards: [], actions: [
        { player: '玩家A', action: 'raise', amount: '3BB' },
        { player: '玩家B', action: 'call' },
      ]},
      { name: 'Flop', communityCards: mockCommunity.slice(0, 3), actions: [
        { player: '玩家A', action: 'bet', amount: '6BB' },
        { player: '玩家B', action: 'call' },
      ]},
      { name: 'Turn', communityCards: [mockCommunity[3]], actions: [
        { player: '玩家A', action: 'check' },
        { player: '玩家B', action: 'bet', amount: '10BB' },
        { player: '玩家A', action: 'call' },
      ]},
      { name: 'River', communityCards: [mockCommunity[4]], actions: [
        { player: '玩家A', action: 'all-in' },
        { player: '玩家B', action: 'fold' },
      ]},
    ],
    potDistribution: [
      { label: hand.winner ? `${hand.winner} (贏家)` : '待結算', amount: Math.round(hand.pot * 0.95) },
      { label: '抽水', amount: Math.round(hand.pot * 0.05) },
    ],
  }
}

export function AdminHandsScreen() {
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterType>('all')
  const [expandedHand, setExpandedHand] = useState<string | null>(null)
  const [detailDrawerHand, setDetailDrawerHand] = useState<MockHand | null>(null)
  const [disputeConfirm, setDisputeConfirm] = useState(false)

  const filtered = mockHands.filter((h) => {
    const q = search.toLowerCase()
    const matchesSearch = !q || h.id.toLowerCase().includes(q) || h.room.toLowerCase().includes(q) || (h.winner?.toLowerCase().includes(q) ?? false)
    const matchesFilter = activeFilter === 'all' || activeFilter === 'today' || activeFilter === h.status
    return matchesSearch && matchesFilter
  })

  return (
    <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="flex flex-col h-full">
      {/* Search */}
      <motion.div variants={fadeInUp} className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋牌局 (ID、房間、玩家)"
            className="w-full h-11 pl-10 pr-4 bg-surface-elevated border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>
      </motion.div>

      {/* Filter Chips */}
      <motion.div variants={fadeInUp} className="px-4 pb-3">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={cn(
                'flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-all border',
                activeFilter === f.id
                  ? 'bg-gold/20 text-gold border-gold/30'
                  : 'bg-surface-elevated text-muted-foreground border-foreground/5 hover:text-foreground'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Results Count */}
      <div className="px-5 pb-2">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">{filtered.length} 場牌局</p>
      </div>

      {/* Hand List */}
      <motion.div variants={staggerContainer} className="flex-1 overflow-y-auto px-4 pb-8 space-y-2.5 scrollbar-hide">
        {filtered.map((hand) => {
          const expanded = expandedHand === hand.id
          const sc = statusConfig[hand.status]
          return (
            <motion.div key={hand.id} variants={cardReveal} className={cn('rounded-2xl glass-card overflow-hidden transition-all', expanded && 'border-gold/30')}>
              <button
                onClick={() => setExpandedHand(expanded ? null : hand.id)}
                className="w-full text-left p-4"
              >
                {/* Top Row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold font-mono text-foreground">#{hand.id}</span>
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', sc.cls)}>{sc.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span className="font-mono">{hand.time}</span>
                  </div>
                </div>

                {/* Middle Row */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Room {hand.room} - ${hand.stakes}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-foreground/5 text-foreground">{hand.result}</span>
                </div>

                {/* Bottom Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold font-mono text-gold">${(hand.pot / 100).toFixed(2)}</span>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="w-3 h-3" />
                      <span>{hand.players}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hand.winner ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400">
                        <Trophy className="w-3 h-3" />
                        {hand.winner}
                      </span>
                    ) : (
                      <span className="text-xs text-sky-400 font-medium">底池凍結中</span>
                    )}
                    <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform duration-200', expanded && 'rotate-180')} />
                  </div>
                </div>
              </button>

              {/* Expanded Detail */}
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-foreground/5"
                  >
                    <div className="p-4 space-y-4 bg-surface-elevated/30">
                      {/* Community Cards */}
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-2">公共牌</p>
                        <div className="flex gap-1.5">{mockCommunity.map((c, i) => <MiniCard key={i} {...c} />)}</div>
                      </div>
                      {/* Player Hole Cards */}
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-2">玩家手牌</p>
                        <div className="space-y-2">
                          {mockPlayerCards.map((p) => (
                            <div key={p.name} className="flex items-center gap-3">
                              <span className="text-xs text-foreground w-16 truncate">{p.name}</span>
                              <div className="flex gap-1">{p.cards.map((c, i) => <MiniCard key={i} {...c} />)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Street Actions */}
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-2">行動摘要</p>
                        <div className="space-y-1 text-xs font-mono text-muted-foreground">
                          {['Preflop: 小明 raise 4BB, Lucky88 call, 阿花 call', 'Flop: 小明 bet 8BB, Lucky88 fold, 阿花 call',
                            'Turn: 小明 check, 阿花 bet 12BB, 小明 call', 'River: 小明 all-in, 阿花 fold'].map((line) => (
                            <p key={line}><span className="text-gold">{line.split(':')[0]}:</span>{line.split(':').slice(1).join(':')}</p>
                          ))}
                        </div>
                      </div>

                      {/* Pot Breakdown */}
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-2">底池明細</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { label: '主池', pct: 0.8 },
                            { label: '抽水', pct: 0.05 },
                            { label: '邊池', pct: 0.15 },
                          ].map((s) => (
                            <div key={s.label} className="p-2 rounded-lg bg-surface-elevated text-center">
                              <p className="text-[10px] text-muted-foreground">{s.label}</p>
                              <p className="text-xs font-bold font-mono text-foreground">${(hand.pot / 100 * s.pct).toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={(e) => { e.stopPropagation(); setDetailDrawerHand(hand) }}
                        className="w-full py-2.5 rounded-xl bg-gold/10 border border-gold/20 text-gold text-xs font-bold flex items-center justify-center gap-1.5"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        查看完整紀錄
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">找不到符合條件的牌局</p>
          </div>
        )}
      </motion.div>

      {/* ── Hand Detail Drawer ── */}
      <Drawer.Root open={detailDrawerHand !== null} onOpenChange={(open) => { if (!open) { setDetailDrawerHand(null); setDisputeConfirm(false) } }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>

              {detailDrawerHand && (() => {
                const handDetail = getHandDetail(detailDrawerHand)
                return (
                  <div className="px-6 pb-8 safe-area-bottom space-y-5">
                    {/* Header */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono text-muted-foreground">{handDetail.id}</span>
                        <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', statusConfig[detailDrawerHand.status].cls)}>
                          {statusConfig[detailDrawerHand.status].label}
                        </span>
                      </div>
                      <Drawer.Title className="font-display text-xl font-semibold text-foreground">
                        牌局 {handDetail.id}
                      </Drawer.Title>
                      <p className="text-sm text-muted-foreground mt-1">{handDetail.timestamp}</p>
                    </div>

                    {/* Street-by-Street Timeline + Pot Distribution */}
                    <HandTimeline
                      streets={handDetail.streets}
                      potDistribution={handDetail.potDistribution}
                      totalPot={detailDrawerHand.pot}
                    />

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {!disputeConfirm ? (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => setDisputeConfirm(true)}
                            className="h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center gap-1.5"
                          >
                            <Flag className="w-3.5 h-3.5" />
                            標記爭議
                          </button>
                          <button
                            onClick={() => {
                              toast({ type: 'success', title: '手牌紀錄已匯出' })
                            }}
                            className="h-12 rounded-xl bg-foreground/5 border border-foreground/10 text-foreground text-xs font-bold flex items-center justify-center gap-1.5"
                          >
                            <Download className="w-3.5 h-3.5" />
                            匯出手牌
                          </button>
                        </div>
                      ) : (
                        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            <p className="text-xs font-bold text-amber-400">確定要將此牌局標記為爭議？</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => setDisputeConfirm(false)}
                              className="h-10 rounded-xl glass-card text-xs font-semibold text-foreground"
                            >
                              取消
                            </button>
                            <button
                              onClick={() => {
                                toast({ type: 'warning', title: '已標記爭議', description: `牌局 ${detailDrawerHand.id} 已標記為爭議` })
                                setDisputeConfirm(false)
                                setDetailDrawerHand(null)
                              }}
                              className="h-10 rounded-xl bg-amber-500 text-white text-xs font-bold"
                            >
                              確認標記
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
